import { Component, OnDestroy, OnInit } from '@angular/core';
import { PrFormConstructor } from '@app/components/pr-form/pr-form.contructor';
import { ProductDao } from '@app/dao/product/product.dao';
import { UserDao } from '@app/dao/user/user.dao';
import { InfoOption } from '@app/forms/subscription/definition/interface/pledge-options';
import { SubscriptionFacadeService } from '@app/forms/subscription/services/subscription-facade.service';
import { Subject, finalize, take, takeUntil } from 'rxjs';
import { setupConditionalValidator, toArray, toNumber } from '../../subscription-form.utils';
import { EaSubscriptionBuilder } from './ea-subscription-form.builder';

const NOMBRE_ADHERENTS_OPTIONS = [
  { value: 'CLIENT', label: 'Client seul' },
  { value: 'LE_COUPLE', label: 'Le couple' },
  { value: 'CLIENT_ET_SES_ENFANTS', label: 'Client et ses enfants' },
  { value: 'LE_COUPLE_ET_SES_ENFANTS', label: 'Le couple et ses enfants' },
];

const NIVEAU_BESOIN_OPTIONS = [
  { value: 'MINIMUM', label: 'Minimum' },
  { value: 'PARTIEL', label: 'Partiel' },
  { value: 'RENFORCE', label: 'Renforcé' },
];

const PROJET_MATERNITE_OPTIONS = [
  { value: 'PRIVE', label: 'Privé' },
  { value: 'PUBLIC', label: 'Public' },
];

const COUVERTURE_ACTUELLE_OPTIONS = [
  { value: 'OUI', label: 'Oui' },
  { value: 'NON', label: 'Non' },
  { value: 'NE_SAIT_PAS', label: 'Ne sait pas' },
];

const NATURE_CONTRAT_EXISTANT_OPTIONS = [
  { value: 'MUTUELLE_INDIVIDUELLE', label: 'Mutuelle individuelle' },
  { value: 'CONTRAT_COLLECTIF', label: 'Contrat collectif employeur' },
  { value: 'CSS', label: 'CSS / CMU-C' },
  { value: 'ACS', label: 'ACS' },
  { value: 'AUTRE', label: 'Autre' },
];

const MOTIF_DEMANDE_OPTIONS = [
  { value: 'RESILIATION', label: 'Résiliation du contrat actuel' },
  { value: 'COMPLEMENT', label: 'Complément de couverture' },
  { value: 'TARIF', label: 'Motif tarifaire' },
  { value: 'AUTRE', label: 'Autre motif' },
];

const ELIGIBILITE_CSS_OPTIONS = [
  { value: 'NON_CONCERNE', label: 'Non concerné' },
  { value: 'VERIFICATION_SOUHAITEE', label: 'Vérification souhaitée' },
];

const ENFANTS_ADHERENTS = ['CLIENT_ET_SES_ENFANTS', 'LE_COUPLE_ET_SES_ENFANTS'];

@Component({
    selector: 'insurance-hypotheses',
    templateUrl: './insurance-hypotheses.component.html',
    styleUrls: ['./insurance-hypotheses.component.scss'],
    standalone: false
})
export class InsuranceHypothesesComponent
  extends EaSubscriptionBuilder
  implements OnInit, OnDestroy
{
  parameters;
  nombreAdherentsOptions = NOMBRE_ADHERENTS_OPTIONS;
  niveauBesoinOptions = NIVEAU_BESOIN_OPTIONS;
  projetMaterniteOptions = PROJET_MATERNITE_OPTIONS;
  optiqueVisionOptions: { label: string; value: string }[] = [];
  dentaireSoinsOptions: { label: string; value: string }[] = [];
  optionWarning: InfoOption = {
    icon: 'warning',
    backgroundColor: '#fae4e4',
    iconColor: '#a00d0d',
    textColor: '#a00d0d',
  };
  enfantsOptions: { label: string; value: string }[] = [];
  conjointNom: string | null = null;
  showEnfants = false;
  showCouvertureDetails = false;
  couvertureActuelleOptions = COUVERTURE_ACTUELLE_OPTIONS;
  natureContratExistantOptions = NATURE_CONTRAT_EXISTANT_OPTIONS;
  motifDemandeOptions = MOTIF_DEMANDE_OPTIONS;
  eligibiliteCssOptions = ELIGIBILITE_CSS_OPTIONS;
  private destroy$ = new Subject<void>();

  constructor(
    protected formConstructor: PrFormConstructor,
    private subscriptionFacade: SubscriptionFacadeService,
    protected productDao: ProductDao,
    private userDao: UserDao,
  ) {
    super(formConstructor);
  }

  ngOnInit() {
    const pledge = this.subscriptionFacade.getPledge();
    this.parameters = pledge?.parameters;
    this.subscriptionFacade.initForm(this, { ...this.parameters });

    this.productDao.getOptiqueVisionRef().pipe(take(1)).subscribe(data => {
      this.optiqueVisionOptions = data.map(({ id, value }) => ({ label: value, value: id }));
    });

    this.productDao.getDentaireSoinsRef().pipe(take(1)).subscribe(data => {
      this.dentaireSoinsOptions = data.map(({ id, value }) => ({ label: value, value: id }));
    });

    // Charger le conjoint pour le RCDA
    this.userDao.getCouple().pipe(take(1)).subscribe(couple => {
      if (couple?.length > 1) {
        this.conjointNom = `${couple[1]?.firstName || ''} ${couple[1]?.lastName || ''}`.trim() || null;
      }
    });

    // Charger les enfants pour le multi-select
    this.userDao.getChildren().pipe(take(1)).subscribe(children => {
      this.enfantsOptions = (children || [])
        .map(c => ({
          label: `${c?.relationUser?.firstName || ''} ${c?.relationUser?.lastName || ''}`.trim() || `Enfant ${c?.relationUser?.id}`,
          value: String(c?.relationUser?.id),
        }))
        .filter(o => !!o.value);
    });

    const params = this.products?.get('0')?.get('parameters');
    const getCtrl = (name: string) => params?.get(name);

    setupConditionalValidator(
      getCtrl('optiqueActif'), [getCtrl('optiqueVisions')],
      value => !!value, this.destroy$,
    );

    setupConditionalValidator(
      getCtrl('operationMyopie'), [getCtrl('budgetOperationMyopie')],
      value => !!value, this.destroy$,
    );

    setupConditionalValidator(
      getCtrl('dentaireActif'), [getCtrl('dentaireSoins')],
      value => !!value, this.destroy$,
    );

    // nombreAdherents : enfantsRattachesIds requis si ENFANTS + enfants disponibles
    setupConditionalValidator(
      getCtrl('nombreAdherents'), [getCtrl('enfantsRattachesIds')],
      value => ENFANTS_ADHERENTS.includes(value) && this.enfantsOptions.length > 0,
      this.destroy$,
    );

    // showEnfants (UI toggle) piloté par valueChanges
    getCtrl('nombreAdherents')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
      this.showEnfants = ENFANTS_ADHERENTS.includes(value);
    });
    this.showEnfants = ENFANTS_ADHERENTS.includes(getCtrl('nombreAdherents')?.value);

    // couvertureActuelle : natureContratExistant + motifDemande requis si OUI
    setupConditionalValidator(
      getCtrl('couvertureActuelle'), [getCtrl('natureContratExistant'), getCtrl('motifDemande')],
      value => value === 'OUI', this.destroy$,
    );

    // showCouvertureDetails (UI toggle)
    getCtrl('couvertureActuelle')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
      this.showCouvertureDetails = value === 'OUI';
    });
    this.showCouvertureDetails = getCtrl('couvertureActuelle')?.value === 'OUI';
  }

  onSubmit() {
    // Normaliser optiqueVisions en tableau avant le submit
    const optiqueVisionsCtrl = this.products?.get('0')?.get('parameters')?.get('optiqueVisions');
    optiqueVisionsCtrl?.setValue(toArray(optiqueVisionsCtrl.value), { emitEvent: false });

    // Résoudre les noms des enfants sélectionnés pour le RCDA
    const enfantsIdsVal = toArray(this.products?.get('0')?.get('parameters')?.get('enfantsRattachesIds')?.value).map(String);
    const enfantsNoms = this.enfantsOptions
      .filter(o => enfantsIdsVal.includes(o.value))
      .map(o => o.label);

    // Mettre à jour enfantsNoms et conjointNom dans le form
    const paramsGroup = this.products?.get('0')?.get('parameters');
    paramsGroup?.get('enfantsNoms')?.setValue(enfantsNoms.length ? enfantsNoms : [], { emitEvent: false });
    paramsGroup?.get('conjointNom')?.setValue(this.conjointNom || null, { emitEvent: false });

    const pledge = this.subscriptionFacade.getPledge();
    const params = paramsGroup?.value;
    if (pledge && params) {
      const ownerId = pledge.owners?.[0]?.ownerId || pledge.owners?.[0]?.id;
      if (ownerId) {
        const body = {
          ownerId,
          accountId: Number(pledge.accountId),
          nombreAdherents: params.nombreAdherents,
          optiqueActif: params.optiqueActif,
          optiqueVisions: toArray(params.optiqueVisions),
          besoinOptiqueLunettes: params.besoinOptiqueLunettes,
          besoinOptiqueLentilles: params.besoinOptiqueLentilles,
          operationMyopie: params.operationMyopie,
          budgetOperationMyopie: toNumber(params.budgetOperationMyopie),
          optiqueNotes: params.optiqueNotes,
          dentaireActif: params.dentaireActif,
          dentaireSoins: toArray(params.dentaireSoins),
          besoinDentaire: params.besoinDentaire,
          orthodontieEnfants: params.orthodontieEnfants,
          dentaireNotes: params.dentaireNotes,
          besoinSoinsCourants: params.besoinSoinsCourants,
          soinsCourantsNotes: params.soinsCourantsNotes,
          besoinHospitalisation: params.besoinHospitalisation,
          projetMaternite: params.projetMaternite,
          hospitalisationNotes: params.hospitalisationNotes,
          medecinesDoucesActif: params.medecinesDoucesActif,
          besoinMedecinesDouces: params.besoinMedecinesDouces,
          medecinesDoucesNotes: params.medecinesDoucesNotes,
          enfantsRattachesIds: toArray(params.enfantsRattachesIds).map(Number),
          couvertureActuelle: params.couvertureActuelle,
          natureContratExistant: params.natureContratExistant,
          motifDemande: params.motifDemande,
          eligibiliteCss: params.eligibiliteCss,
          informeRisqueCumul: params.informeRisqueCumul,
          notesGenerales: params.notesGenerales,
        };
        // Sync hypothèses puis soumission garantie APRÈS la persistance (succès ou échec)
        this.productDao.saveMutuelleHypotheses(Number(pledge.accountId), body)
          .pipe(
            take(1),
            finalize(() => this.subscriptionFacade.submitPledge(this)),
          )
          .subscribe({
            error: () =>
              this.notificationService.error(
                'La sauvegarde des hypothèses mutuelle a échoué',
              ),
          });
        return;
      }
    }
    this.subscriptionFacade.submitPledge(this);
  }

  prev() {
    this.subscriptionFacade.previousStep();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
